import {
  BellIcon,
  CogIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  LanguageIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
  Switch,
} from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  changeLanguage,
  fetchCountries,
  getAllSubregions,
  getProfileForSetting,
  updateActivationRequired,
  updateNotificationSetting,
} from "../../services/api";
import "../../styles/settings.css";
import {
  EventPreferenceDto,
  NotificationSettingRequestDto,
  Profile,
} from "../../types/types";

interface SettingsData {
  language: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    marketing: boolean;
    regions: string[];
  };
  theme: "light" | "dark" | "system";
  privacy: {
    profileVisible: boolean;
    showEmail: boolean;
    showActivity: boolean;
    dataCollection: boolean;
  };
}

interface Region {
  code: string;
  name: string;
}

const ALL_COUNTRIES_KEY = "All_Countries";

const Settings: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("language");
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [regionOptions, setRegionOptions] = useState<Region[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const { t, i18n } = useTranslation("settings");
  const [activityRequired, setActivityRequired] = useState<boolean>(false);
  const savedProfile = localStorage.getItem("loginResponse");
  const parsedProfile = savedProfile ? JSON.parse(savedProfile) : null;
  const Role = parsedProfile?.profileType;
  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const defaultEventPreferences: EventPreferenceDto[] = [
    { eventName: "CARD_PUBLISH", emailNotify: false, pushNotify: false },
    { eventName: "DEAL_PROPOSE", emailNotify: false, pushNotify: false },
    { eventName: "DEAL_CONFIRM", emailNotify: false, pushNotify: false },
    { eventName: "CONTACT_INVITE", emailNotify: false, pushNotify: false },
    { eventName: "CONTACT_ACCEPT", emailNotify: false, pushNotify: false },
  ];

  const [eventPreferences, setEventPreferences] = useState<
    EventPreferenceDto[]
  >(defaultEventPreferences);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileSettingId, setProfileSettingId] = useState<number | null>(null);

  const [errors, setErrors] = useState({
    country: "",
  });

  const [settings, setSettings] = useState<SettingsData>({
    language: i18n.language || "en",
    notifications: {
      email: true,
      push: true,
      sms: false,
      marketing: false,
      regions: [],
    },
    theme: "system",
    privacy: {
      profileVisible: true,
      showEmail: false,
      showActivity: true,
      dataCollection: true,
    },
  });

  const languageNames: { [key: string]: string } = {
    en: t("language_names.en", { defaultValue: "English" }),
    de: t("language_names.de", { defaultValue: "Deutsch" }),
    hr: t("language_names.hr", { defaultValue: "Hrvatski" }),
  };

  const sidebarItems = [
    {
      key: "language",
      label: t("language"),
      icon: <LanguageIcon className="w-5 h-5" />,
    },
    {
      key: "notifications",
      label: t("notifications"),
      icon: <BellIcon className="w-5 h-5" />,
    },
    ...(Role === "ADMIN"
      ? [
          {
            key: "admin controll",
            label: t("admin_control"),
            icon: <WrenchScrewdriverIcon className="w-5 h-5" />,
          },
        ]
      : []),
    {
      key: "privacy",
      label: t("privacy"),
      icon: <ShieldCheckIcon className="w-5 h-5" />,
    },
    {
      key: "about",
      label: t("about"),
      icon: <InformationCircleIcon className="w-5 h-5" />,
    },
    {
      key: "terms",
      label: t("terms"),
      icon: <DocumentTextIcon className="w-5 h-5" />,
    },
  ];

  const validateForm = () => {
    const newErrors = {
      country: selectedCountries.length > 0 ? "" : t("at_least_one_country"),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === "");
  };

  const countryOptions = [
    { label: t("all_countries"), value: ALL_COUNTRIES_KEY },
    ...countries.map((c) => ({ label: c, value: c })),
  ];

  const formatEventName = (eventName: string): string => {
    return eventName
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countryData = await fetchCountries();
        const withAll = [ALL_COUNTRIES_KEY, ...countryData];
        setCountries(withAll);

        setLoadingRegions(true);
        const allSubregions = await getAllSubregions();
        const formattedRegions: Region[] = allSubregions.map((subregion) => ({
          code: subregion,
          name: subregion,
        }));
        setRegionOptions(formattedRegions);
      } catch (error) {
        toast.error(t("failed_load_data"));
      } finally {
        setLoadingRegions(false);
      }
    };

    loadInitialData();

    const loginResponse = JSON.parse(
      localStorage.getItem("loginResponse") || "{}"
    );
    const loggedInUserName = loginResponse.username;

    if (!loggedInUserName) return;

    getProfileForSetting(loggedInUserName)
      .then((data) => {
        setProfile(data);

        if (data.profileSetting?.activationSetting) {
          setActivityRequired(
            data.profileSetting.activationSetting.activationRequired
          );
        }

        const setting = data.profileSetting?.notificationSetting;
        if (setting) {
          setProfileSettingId(data.profileSetting?.id ?? null);
          setSelectedCountries(setting.countries || []);
          setSelectedRegions(setting.regions || []);

          const updatedPreferences = defaultEventPreferences.map((pref) => {
            const match = setting.events?.find((e: any) => e.name === pref.eventName);
            return {
              ...pref,
              emailNotify: match?.emailNotify ?? false,
              pushNotify: match?.pushNotify ?? false,
            };
          });
          setEventPreferences(updatedPreferences);
        }
      })
      .catch((err) => {
        toast.error(t("failed_load_profile"));
      });
  }, [t]);

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error(t("validation_failed"));
      return;
    }

    if (!profileSettingId) {
      toast.error(t("missing_profile_setting_id"));
      return;
    }

    const loginResponse = JSON.parse(
      localStorage.getItem("loginResponse") || "{}"
    );
    const loggedInUserName = loginResponse.username;

    const payload: NotificationSettingRequestDto = {
      countries: selectedCountries,
      regions: selectedRegions,
      profileSettingId,
      eventPreferences,
    };

    try {
      await updateNotificationSetting(loggedInUserName, payload);
      toast.success(t("notifications_save_success"));
    } catch {
      toast.error(t("notifications_save_failed"));
    }
  };

  const handleLanguageChange = async (value: string) => {
    try {
      await i18n.changeLanguage(value);
      await changeLanguage(value);
      setSettings((prev) => ({ ...prev, language: value }));
      localStorage.setItem("i18nextLng", value);
      localStorage.setItem(
        "appSettings",
        JSON.stringify({
          ...settings,
          language: value,
          savedAt: new Date().toISOString(),
        })
      );
      toast.success(t("language_update_success"));
    } catch (error) {
      toast.error(t("language_update_failed"));
    }
  };

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setSettings((prev) => ({ ...prev, language: lng }));
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  const renderLanguageSection = () => (
    <Card className="animate-slide-in-up">
      <CardHeader>
        <div className="flex items-center gap-2">
          <LanguageIcon className="w-5 h-5 text-[#1E3A8A]" />

          <h3 className="text-lg font-semibold">{t("language")}</h3>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-gray-600 text-sm">{t("language_description")}</p>
        <Select
          label={t("select_language")}
          selectedKeys={new Set([settings.language])}
          onSelectionChange={(keys) => {
            const selectedKey = Array.from(keys)[0] as string;
            if (selectedKey) {
              handleLanguageChange(selectedKey);
            }
          }}
          className="max-w-xs"
          startContent={<LanguageIcon className="w-4 h-4" />}
        >
          {Object.entries(languageNames).map(([key, name]) => (
            <SelectItem key={key}>{name}</SelectItem>
          ))}
        </Select>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>{t("current_language")}:</strong>{" "}
            {languageNames[settings.language]}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            {t("language_code")}: {settings.language}
          </p>
        </div>
        <div className="mt-4 p-3 bg-green-50 rounded-lg">
          <p className="text-sm text-green-700">
            💡 <strong>{t("language_tip")}:</strong> {t("language_tip")}
          </p>
        </div>
      </CardBody>
    </Card>
  );

  const renderNotificationSection = () => (
    <Card className="animate-slide-in-up">
      <CardHeader>
        <div className="flex items-center gap-2">
          <BellIcon className="w-5 h-5 text-[#1E3A8A]" />
          <h3 className="text-lg font-semibold">{t("notifications")}</h3>
        </div>
      </CardHeader>
      <CardBody className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-700">
              {t("countries")}
            </label>
            <Select
              placeholder={t("select_countries")}
              selectionMode="multiple"
              selectedKeys={new Set(selectedCountries)}
              onSelectionChange={(keys) => {
                const values = Array.from(keys).map(String);

                setSelectedCountries((previousCountries) => {
                  const hasAllCountries = values.includes(ALL_COUNTRIES_KEY);
                  const hadAllCountries =
                    previousCountries.includes(ALL_COUNTRIES_KEY);

                  if (!hasAllCountries) {
                    return values;
                  }

                  if (!hadAllCountries) {
                    return [ALL_COUNTRIES_KEY];
                  }

                  return values.filter(
                    (country) => country !== ALL_COUNTRIES_KEY
                  );
                });

                setErrors((prev) => ({ ...prev, country: "" }));
              }}
              size="sm"
              startContent={<GlobeAltIcon className="w-4 h-4" />}
              renderValue={(items) =>
                items
                  .map((item) =>
                    item.key === ALL_COUNTRIES_KEY
                      ? "All Countries"
                      : item.textValue
                  )
                  .join(", ")
              }
            >
              {countries.map((country) => (
                <SelectItem key={country} textValue={country}>
                  {country === ALL_COUNTRIES_KEY ? "All Countries" : country}
                </SelectItem>
              ))}
            </Select>

            {errors.country && (
              <p className="text-xs text-red-500">{errors.country}</p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Card className="p-0 bg-gray-50 border border-gray-200">
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                        {t("notification_type")}
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">
                        {t("email_notification")}
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">
                        {t("push_notification")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventPreferences.map((pref, index) => (
                      <tr
                        key={`${pref.eventName}-${index}`}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4 text-sm text-gray-700 flex items-center gap-1">
                          {formatEventName(pref.eventName)}
                          <div className="relative group">
                            <InformationCircleIcon className="w-4 h-4 text-red-500 cursor-pointer" />
                            <div className="absolute z-10 left-6 top-0 w-48 p-2 bg-white border border-gray-200 shadow-md text-xs text-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                              {t(`${pref.eventName.toLowerCase()}_description`)}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-center">
                          <div className="flex justify-center">
                            <Switch
                              size="sm"
                              isSelected={pref.emailNotify}
                              onValueChange={(val) => {
                                const updated = [...eventPreferences];
                                updated[index] = {
                                  ...updated[index],
                                  emailNotify: val,
                                };
                                setEventPreferences(updated);
                              }}
                            />
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-center">
                          <div className="flex justify-center">
                            <Switch
                              size="sm"
                              isSelected={pref.pushNotify}
                              onValueChange={(val) => {
                                const updated = [...eventPreferences];
                                updated[index] = {
                                  ...updated[index],
                                  pushNotify: val,
                                };
                                setEventPreferences(updated);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="mt-6">
          <Button color="primary" size="sm" onPress={handleSave}>
            {t("save_notifications")}
          </Button>
        </div>
      </CardBody>
    </Card>
  );

  const renderEmptySection = (title: string, description: string) => (
    <Card className="animate-slide-in-up">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-gray-300 rounded" />
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-gray-600 text-sm">{description}</p>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CogIcon className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm">{t("under_development")}</p>
        </div>
      </CardBody>
    </Card>
  );

  const renderAdminControlSection = () => {
    const handleSwitchChange = async (val: boolean) => {
      setActivityRequired(val);

      try {
        const loginResponse = JSON.parse(
          localStorage.getItem("loginResponse") || "{}"
        );
        const username = loginResponse.username;
        if (!username) return;

        const msg = await updateActivationRequired(username);
        toast.success(msg || t("activation_update_success"));
      } catch (error: any) {
        setActivityRequired(!val);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            t("activation_update_failed")
        );
      }
    };
    return (
      <Card className="animate-slide-in-up">
        <CardHeader>
          <div className="flex items-center gap-2">
            <WrenchScrewdriverIcon className="w-5 h-5 text-[#1E3A8A]" />
            <h3 className="text-lg font-semibold">{t("admin_control")}</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-gray-600 text-sm">
            {t("admin_control_description", {
              defaultValue: "Manage admin-specific settings here.",
            })}
          </p>
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-700">
              {t("activation_required")}
            </div>
            <div className="flex items-center justify-center w-16">
              <Switch
                isSelected={activityRequired}
                onValueChange={handleSwitchChange}
              />
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case "language":
        return renderLanguageSection();
      case "notifications":
        return renderNotificationSection();
      case "admin controll":
        return renderAdminControlSection();
      case "privacy":
        return renderEmptySection(
          t("privacy"),
          t("privacy_description", {
            defaultValue: "Control your privacy and data sharing preferences.",
          })
        );
      case "about":
        return renderEmptySection(
          t("about"),
          t("about_description", {
            defaultValue: "Learn more about the application and its features.",
          })
        );
      case "terms":
        return renderEmptySection(
          t("terms"),
          t("terms_description", {
            defaultValue: "Review our terms of service and privacy policy.",
          })
        );
      default:
        return renderLanguageSection();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8 animate-slide-in-up">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("settings_title")}
          </h1>
          <p className="text-gray-600">{t("settings_description")}</p>
        </div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-3">
            <Card className="sticky top-6 animate-slide-in-left">
              <CardBody className="p-0">
                <div className="space-y-1">
                  {sidebarItems.map((item) => (
                    <Button
                      key={item.key}
                      variant={activeSection === item.key ? "solid" : "light"}
                      color={activeSection === item.key ? "primary" : "default"}
                      className={`w-full justify-start h-12 px-4 ${
                        activeSection === item.key
                          ? "bg-[#1E3A8A] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      startContent={item.icon}
                      onPress={() => setActiveSection(item.key)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
          <div className="col-span-9">
            <div className="space-y-6">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
